//Import Mixpanel SDK
import mixpanel from "mixpanel-browser"
import { config } from "~/lib/config"

/**
 * Registered as super properties AND written to the profile — replay records
 * never carry super properties, so the profile copy is what makes replays
 * filterable. `environment` matters because staging and production share one
 * token, so without it every number mixes test traffic with real borrowers.
 */
const surfaceProperties = () => ({
  app: "dashboard",
  environment: config.env.mode,
  platform: "web",
})

type PendingIdentity = { id: string; displayName: string; email: string }

let initialized = false

/**
 * `syncIdentity` can fire before `root.tsx`'s mount effect initialises Mixpanel
 * — the query-cache subscriber in `store/queryClient.ts` runs at module scope
 * and a restored `['user']` query resolves first. Its dedup then suppresses
 * every later call, so without replaying the identity here the whole page load
 * stays anonymous.
 */
let pendingIdentity: PendingIdentity | null = null

export const initMixPanel = () => {
  // No token locally (.env carries only the Sentry DSN), and initialising
  // without one leaves the library unusable — every later call then throws.
  if (!config.env.mixpanelToken) {
    return
  }
  mixpanel.init(config.env.mixpanelToken, {
    debug: config.env.isDevelopment,
    autocapture: true,
    track_pageview: true,
    persistence: "localStorage",
    record_heatmap_data: true,
    record_sessions_percent: 100, // we got 20k a month we don't have close to that many users yet
    // Defaults are `'*'` and `'img, video, audio'`, which render a replay as
    // grey boxes. Mask individual surfaces with `fs-mask` (text) or `fs-exclude`
    // (element) — Mixpanel's default class patterns already match FullStory's
    // names, so one class covers both vendors. Password inputs always masked.
    record_mask_text_selector: "",
    record_block_selector: "",
  })

  // The old mobile app reports through Segment and registers none of these, so
  // `app is not set` isolates its historical events from both current apps.
  mixpanel.register(surfaceProperties())
  initialized = true

  const pending = pendingIdentity
  pendingIdentity = null
  if (pending) {
    setMixpanelUser(pending.id, pending.displayName, pending.email)
  }
}

/**
 * `identify` is idempotent, but `reset()` assigns a new distinct_id — it must
 * only run on a real sign-out, which `syncIdentity`'s dedup already enforces.
 * `alias()` is deliberately unused; Simplified ID Merge links the pre-login
 * anonymous events (including "Registration Submitted") to this profile.
 */
export const setMixpanelUser = (
  id: string | null,
  displayName: string,
  email: string
) => {
  if (!initialized) {
    // Held rather than dropped — see `pendingIdentity`. A sign-out before init
    // clears any queued identity; there is no session to reset yet.
    pendingIdentity = id ? { id, displayName, email } : null
    return
  }
  try {
    if (id) {
      mixpanel.identify(id)
      mixpanel.people.set({
        $email: email,
        $name: displayName,
        ...surfaceProperties(),
      })
    } else {
      mixpanel.reset()
    }
  } catch {
    // Blocked by an extension — must never break the auth flow.
  }
}
